import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  IconButton,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CommonShimmer from "../../../../Common/CommonShimmer/CommonShimmer";
import CommonPagination from "../../../../Common/CommonPagination/CommonPagination";
import { postMediMediaSubList } from "../../../../../Core/Apicall";
import moment from "moment";
import ChatDrawer from "./ChatDrawer";

interface ClaimUser {
  _id: string;
  fullName: string;
  profileImagePath?: string;
  reportedAt: string;
  reason?: string;
}

interface MediaLeftMenuProps {
  mediaId: string;
}

const MediaLeftMenu: React.FC<MediaLeftMenuProps> = ({ mediaId }) => {
  const [users, setUsers] = useState<ClaimUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<ClaimUser | null>(null);
  const [openChat, setOpenChat] = useState<boolean>(false);

  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await postMediMediaSubList({
        mediaId,
        offset: page,
        limit: rowsPerPage,
      });

      if (res.success) {
        setUsers(res.data || []);
        setTotalItems(res.totalCount || 0);
      }
    } catch (error) {
      console.error("❌ Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mediaId) {
      fetchUsers();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [mediaId, page, rowsPerPage]);

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0px 2px 10px rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: 600, mb: 2, color: "#222" }}
      >
        Media Users
      </Typography>

      {/* Loading */}
      {loading ? (
        <Box p={2}>
          <CommonShimmer />
        </Box>
      ) : users.length === 0 ? (
        <Typography sx={{ textAlign: "center", py: 3, color: "#777" }}>
          No users found.
        </Typography>
      ) : (
        <TableContainer style={{ maxHeight: 600, overflowY: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Avatar</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reported At</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                  Chat
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user._id}
                  hover
                  sx={{
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <TableCell>
                    <Avatar
                      src={user.profileImagePath}
                      alt={user.fullName}
                      sx={{
                        width: 42,
                        height: 42,
                        border: "2px solid #eee",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontWeight: 500 }}>
                    {user.fullName}
                  </TableCell>

                  <TableCell sx={{ color: "#555" }}>
                    {user.reason || "—"}
                  </TableCell>

                  <TableCell sx={{ color: "#666" }}>
                    {moment(user.reportedAt).format("DD MMM YYYY")}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      sx={{ color: "black" }}
                      color="primary"
                      onClick={() => {
                        setSelectedUser(user);
                        setOpenChat(true);
                      }}
                    >
                      <ChatBubbleOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {users.length > 0 && (
        <Box mt={2}>
          <CommonPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(rows) => {
              setRowsPerPage(rows);
              setPage(1);
            }}
          />
        </Box>
      )}

      {/* Chat Drawer */}
      {openChat && (
        <ChatDrawer
          open={openChat}
          onClose={() => setOpenChat(false)}
          user={selectedUser}
        />
      )}
    </Paper>
  );
};

export default MediaLeftMenu;
